import { Injectable } from '@angular/core';

/**
 * Set by the offer selector as document.cookie `upgradeOption` (JSON string).
 * Post-upgrade success uses sessionStorage, not a cookie `upgradeDestination`.
 */
export interface UpgradeCookie {
  selectedQuantity?: number;
  upgradedQuantity?: number;
  upgradeSavings?: number;
  uom?: string;
  upgradeDestination?: string;
  hideUpgradeCta?: boolean;
}

/** Keep in sync with the offer selector that sets the cookie. */
export const UPGRADE_COOKIE_NAME = 'upgradeOption';

export interface UpgradeViewState {
  selectedQuantity: number;
  upgradedQuantity: number;
  uom: string;
  upgradeSavings: number | null;
  upgradeDestination: string;
  hideUpgradeCta: boolean;
}

@Injectable({ providedIn: 'root' })
export class UpgradeService {
  private static readonly SUCCESS_SESSION_KEY = 'upgradeShowSuccess';
  private static readonly PREV_LOCATION_KEY = 'upgradePreviousLocation';

  /** Read and parse the upgrade option cookie; always returns a full state with safe defaults. */
  getStateFromCookie(): UpgradeViewState {
    const raw = this.readCookieValue(UPGRADE_COOKIE_NAME);
    const out: UpgradeViewState = {
      selectedQuantity: 1,
      upgradedQuantity: 0,
      uom: 'boxes',
      upgradeSavings: null,
      upgradeDestination: '',
      hideUpgradeCta: false,
    };
    if (!raw || !raw.trim()) {
      return out;
    }
    let data: UpgradeCookie;
    try {
      data = JSON.parse(raw.trim()) as UpgradeCookie;
    } catch (e) {
      console.warn('[GH] upgradeOption cookie is not valid JSON. Raw length:', raw.length, e);
      return out;
    }
    if (data.selectedQuantity != null) {
      const q =
        typeof data.selectedQuantity === 'number'
          ? data.selectedQuantity
          : parseInt(String(data.selectedQuantity).trim(), 10);
      if (!Number.isNaN(q) && q >= 0) {
        out.selectedQuantity = Math.floor(q);
      }
    }
    if (data.upgradedQuantity != null) {
      const n =
        typeof data.upgradedQuantity === 'number'
          ? data.upgradedQuantity
          : parseInt(String(data.upgradedQuantity).trim(), 10);
      if (!Number.isNaN(n) && n >= 0) {
        out.upgradedQuantity = Math.floor(n);
      }
    }
    if (typeof data.upgradeDestination === 'string' && data.upgradeDestination.trim().length > 0) {
      out.upgradeDestination = data.upgradeDestination.trim();
    }
    if (data.upgradeSavings != null) {
      const m =
        typeof data.upgradeSavings === 'number'
          ? data.upgradeSavings
          : parseFloat(String(data.upgradeSavings).trim());
      if (!Number.isNaN(m)) {
        out.upgradeSavings = m;
      }
    }
    if (typeof data.uom === 'string' && data.uom.trim().length > 0) {
      out.uom = data.uom.trim();
    }
    if (data.hideUpgradeCta === true) {
      out.hideUpgradeCta = true;
    }
    return out;
  }

  /**
   * Returns true once per successful upgrade handoff, then clears the key.
   * Should set UI “confirmation” when true and `showUpgradeBox` is on.
   */
  consumePostUpgradeSuccessSessionFlag(): boolean {
    let flag: string | null = null;
    try {
      flag = sessionStorage.getItem(UpgradeService.SUCCESS_SESSION_KEY);
    } catch (e) {
      return false;
    }
    if (flag === '1') {
      try {
        sessionStorage.removeItem(UpgradeService.SUCCESS_SESSION_KEY);
      } catch (e) {

      }
      return true;
    }
    return false;
  }

  /**
   * Stores session for post-upgrade + back/uncheck, then navigates to /fst/{upgradeDestination} with
   * the current search + params. Requires `upgradeDestination` in cookie (re-read in caller or pass from state).
   */
  startFstUpgradeNavigation(upgradeDestination: string): void {
    const returnPath =
      window.location.pathname + (window.location.search || '') + (window.location.hash || '');
    try {
      sessionStorage.setItem(UpgradeService.SUCCESS_SESSION_KEY, '1');
      sessionStorage.setItem(UpgradeService.PREV_LOCATION_KEY, returnPath);
    } catch (e) {
      console.log('[GH] upgrade sessionStorage', e);
    }
    window.location.href = this.buildFstUrl(upgradeDestination);
  }

  /**
   * Revert/uncheck: go to the stored pre-upgrade path+query+hash (set when upgrade was started).
   * @returns false if nothing was stored (should re-check the checkbox in UI)
   */
  navigateOnUpgradeUncheck(): boolean {
    let prevLoc: string | null = null;
    try {
      prevLoc = sessionStorage.getItem(UpgradeService.PREV_LOCATION_KEY);
    } catch (e) {
      console.log('[GH] upgrade sessionStorage read', e);
    }
    if (prevLoc === null || prevLoc.length === 0) {
      console.warn('[GH] upgrade: no previous location in session; URL unchanged');
      return false;
    }
    try {
      sessionStorage.removeItem(UpgradeService.PREV_LOCATION_KEY);
    } catch (e) {
      /* ignore */
    }
    if (prevLoc.startsWith('http://') || prevLoc.startsWith('https://')) {
      window.location.href = prevLoc;
      return true;
    }
    const path = prevLoc.startsWith('/') ? prevLoc : `/${prevLoc}`;
    window.location.href = window.location.origin + path;
    return true;
  }

  private buildFstUrl(upgradeDestinationId: string): string {
    const clean = upgradeDestinationId
      .trim()
      .replace(/^\/+/, '')
      .split('/')
      .filter((p) => p.length > 0)
      .map((p) => encodeURIComponent(p))
      .join('/');
    const q = window.location.search || '';
    const h = window.location.hash || '';
    return `${window.location.origin}/fst/${clean}${q}${h}`;
  }

  private readCookieValue(name: string): string | null {
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    if (!match) {
      return null;
    }
    let v = match[1];
    try {
      v = decodeURIComponent(v);
    } catch {

    }
    return v;
  }
}
